/* eslint-disable no-sparse-arrays */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react-hooks/exhaustive-deps */
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  BoltIcon,
  BuildingOfficeIcon,
  ChatBubbleBottomCenterTextIcon,
  HomeIcon,
  PlusCircleIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon
} from "@heroicons/react/24/outline";
import { AppShell, Indicator, NavLink as MantineNavLink, RingProgress, ScrollArea, Text } from "@mantine/core";
import { useAuthInfo, useOrgHelper, useRedirectFunctions } from "@propelauth/react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { queryClient, useInbox } from "../../../hooks/QueryHooks";
import ViewIconDisplay from "../../../shared/components/ViewIconDisplay";
import AvatarMenu from "./AvatarMenu";
import TopSearchBar from "./TopSearchBar";
import { useHotkeys } from "@mantine/hooks";
import { spotlight } from "@mantine/spotlight";
// Styles defined inline as needed

const LeftSideBar = ({
  views,
  menu,
  setMenu,
  workspace,
}: {
  views: any[];
  menu: boolean;
  setMenu: any;
  workspace: any;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
    // Open spotlight via ⌘/Ctrl + K as well
  useHotkeys([['mod+K', () => spotlight.open()]]);
  const { orgHelper } = useOrgHelper();
  const orgs: any = orgHelper?.getOrgs();
  const params = useParams();
  const selectedWorkspace: any = useMemo(
    () =>
      orgs?.find(
        (item: any) => item.orgMetadata.unique_identifier === params.workspaceId
      ) || undefined,
    [orgs?.length, params.workspaceId]
  );

  const [opened, setOpened] = useState<Record<string, boolean>>({
    accounts: false,
    users: false,
  });

  const [count, setCount] = useState<number>(0);

  const authInfo = useAuthInfo();
  const user = authInfo.user?.userId;
  const redirect = useRedirectFunctions();

  useInbox({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
    entityRelation: "accounts",
    notificationType: "inbox",
    owner: [user || "null"],
    retry: 0,
    onSuccess: (data: any) => {
      setCount(data?.length || 0);
    },
    onError: () => {
      queryClient.setQueryData(
        [
          "inbox",
          workspace?.id,
          undefined,
          "accounts",
          undefined,
          undefined,
          undefined,
          undefined,
          "inbox",
          [user || "null"],
        ],
        (oldData: any) => ({
          ...oldData,
          data: {
            data: {
              results: [],
            },
          },
        })
      );
    },
  });

  const defaultView = useMemo(
    () => views?.filter((item: any) => item?.is_precreated)?.reduce((ob: any, val: any) => {
      const newOb = { ...ob };
      newOb[val?.entity_relation] = val;
      return newOb;
    }, {} as any),
    [views]
  );

  const handleToggle = (key: string) => {
    if (key === 'accounts') {
      setOpened((prev: Record<string, boolean>) => ({
        users: false,
        accounts: !prev.accounts,
      }));
    }
    if (key === 'users') {
      setOpened((prev: Record<string, boolean>) => ({
        accounts: false,
        users: !prev.users,
      }));
    }
  };

  // Filter views by entity relationship and owner
  const filterViews = (entityRelation: string, isOwner: boolean | null = null) => {
    if (!views) return [];
    
    // If isOwner is null, return all non-precreated views for that entity
    if (isOwner === null) {
      return views.filter((item: any) =>
        item.entity_relation === entityRelation &&
        !item.is_precreated
      );
    }
    
    // Otherwise filter by owner as before
    return views.filter((item: any) =>
      item.entity_relation === entityRelation &&
      !item.is_precreated &&
      (isOwner ? item.owner_id === user : item.owner_id !== user)
    );
  };
  
  // Check if a specific view or any view with the given entity type is active
  const isActiveRoute = (viewId: string) => 
    location.pathname === `/home/${params.workspaceId}/view/${viewId}`;
  
  // Get position of active view in the list (for the colored connector line)
  const getActiveViewPosition = (entityType: string) => {
    // Is default view active?
    if (defaultView?.[entityType] && isActiveRoute(defaultView[entityType].id)) {
      return 0;
    }
    
    // Check other views
    const entityViews = filterViews(entityType);
    const activeIndex = entityViews.findIndex((view) => isActiveRoute(view.id));
    
    if (activeIndex !== -1) {
      return activeIndex + 1; // +1 because default view is at position 0
    }
    
    // No view active
    return -1;
  };

  return (
    <>
      <AppShell.Section pt="lg" px="sm">
        <AvatarMenu
          name={selectedWorkspace?.orgName}
          menu={menu}
          desc={workspace?.tracking_website_domain}
          img={workspace?.workspace_logo}
        />
        <div
          onClick={() => {
            setMenu((state: any) => !state);
          }}
          className="w-[30px] h-[50px] absolute -right-4 bottom-5"
        >
          <ChevronDownIcon
            className={`${menu ? "rotate-90" : "-rotate-90"}
                ease-linear cursor-pointer fill-purple-400 border rounded-full bg-gray-500 hover:bg-gray-800 duration-100 w-full p-1`}
          />
        </div>
      </AppShell.Section>
      <AppShell.Section px="md">
      {menu && <TopSearchBar />}
      </AppShell.Section>
      <AppShell.Section grow style={{ minHeight: 0 }}>
        <ScrollArea h="100%" type="auto" scrollbarSize={6}>
          <div className="pb-4">
            {menu && (
              <div className="flex items-center px-3 mt-4 gap-2">
                <Text size="9px" fw={500} c="dimmed">
                  Dashboard
                </Text>
              </div>
            )}

            {!workspace?.getting_started_guide_dismissed && (
                <MantineNavLink
                  active={location.pathname.includes('getting-started')}
                  classNames={{
                    body: !menu ? 'hidden' : '',
                    section: !menu ? 'mx-auto' : '',
                    root: !menu ? 'items-center justify-center py-4' : '',
                  }}
                  leftSection={
                    <RingProgress  
                      size={20}
                      thickness={3}
                      sections={[{ value: 100, color: count === 100 ? "gray.5" : "main.5" }]}
                    />
                  }
                  onClick={() => navigate('getting-started')}
                  label={menu ? "Getting Started" : undefined}
                />
            )}
            <MantineNavLink
              classNames={{
                body: !menu ? 'hidden' : '',
                  section: !menu ? 'mx-auto' : '',
                root: !menu ? 'items-center justify-center py-4' : '',
              }}
              leftSection={<HomeIcon className="w-5 h-5" />}
              onClick={() => navigate(`/home/${params.workspaceId}/headquarters`)}
              label={menu ? "Home" : undefined}
              active={location.pathname.includes('headquarters')}
            />

            {menu && (
              <div className="flex items-center px-3 mt-4 gap-2">
                <Text size="9px" fw={500} c="dimmed">
                  Workspace
                </Text>
              </div>
            )}
            <div className="flex-1 min-h-0">
          {defaultView?.accounts && (
            filterViews('accounts').length > 0 && menu ? (
              <MantineNavLink
                classNames={{
                  collapse: 'relative',
                }}
                leftSection={<BuildingOfficeIcon className="w-4 h-4" />}
                label={menu ? "Accounts" : undefined}
                onClick={() => { handleToggle('accounts'); }}
                opened={opened.accounts}
                active={location.pathname.includes('/view/') && views.some(view => view.entity_relation === 'accounts' && location.pathname.includes(`/view/${view.id}`))}
                childrenOffset={0}
              >
                <MantineNavLink
                  classNames={{
                    root: `${location.pathname === `/home/${params.workspaceId}/view/${defaultView.accounts.id}` ? 'after:border-primary-200' : 'after:border-gray-200'} pl-8 after:border-l-2 after:border-b-2 relative after:absolute after:top-3.5 after:left-4 after:w-2.5 after:h-1.5 after:z-10 after:rounded-bl-full`,
                    label: 'capitalize',
                  }}
                  leftSection={<BuildingOfficeIcon className="w-3.5 h-3.5" />}
                  label={menu ? "All Accounts" : undefined}
                  onClick={() => navigate(`/home/${params.workspaceId}/view/${defaultView.accounts.id}`)}
                  active={location.pathname === `/home/${params.workspaceId}/view/${defaultView.accounts.id}`}
                />
                {/* Custom vertical connector with dynamic coloring */}
                {menu && (
                  <div className="absolute left-[16px] top-0 bottom-4 w-0.5 z-20">
                    {(() => {
                      const activePosition = getActiveViewPosition('accounts');
                      // Calculate position for colored line based on active view
                      
                      // If an item is active, render split colored line
                      if (activePosition >= 0) {
                        const activePositionPx = activePosition * 35;
                        return (
                          <>
                            <div 
                              className="absolute left-0 w-full z-10 bg-primary-200" 
                              style={{ top: 0, height: `${activePositionPx + 18}px` }}
                            />
                            <div 
                              className="absolute left-0 w-full bg-gray-200" 
                              style={{ top: `${activePositionPx + 10}px`, bottom: 0 }}
                            />
                          </>
                        );
                      }
                      
                      // Otherwise render full gray line
                      return <div className="absolute inset-0 bg-gray-200" />;
                    })()}
                  </div>
                )}
                
                {filterViews('accounts').length > 0 && menu && (
                  <>
                    {filterViews('accounts').map((view: any) => (
                      <MantineNavLink
                        key={view.id}
                        classNames={{
                          root: `${location.pathname === `/home/${params.workspaceId}/view/${view.id}` ? 'after:border-primary-200' : 'after:border-gray-200'} pl-8 capitalize after:border-l-2 after:border-b-2 relative after:absolute after:top-3.5 after:left-4 after:w-2.5 after:h-1.5 after:z-10 after:rounded-bl-full`,
                          label: 'capitalize'
                        }}
                        leftSection={
                          view.icon_key || view.view_icon ? (
                            <ViewIconDisplay 
                              iconType="built_in" 
                              iconKey={view.icon_key || view.view_icon} 
                              className="h-3.5 w-3.5 flex-shrink-0" 
                            />
                          ) : (
                            <BuildingOfficeIcon className="w-3.5 h-3.5" />
                          )
                        }
                        label={menu ? view.view_name : undefined}
                        onClick={() => navigate(`/home/${params.workspaceId}/view/${view.id}`)}
                        active={location.pathname === `/home/${params.workspaceId}/view/${view.id}`}
                      />
                    ))}
                  </>
                )}
              </MantineNavLink>
            ) : (
              <MantineNavLink
              classNames={{
                body: !menu ? 'hidden' : '',
                  section: !menu ? 'mx-auto' : '',
                root: !menu ? 'items-center justify-center py-4' : '',
              }}
                leftSection={<BuildingOfficeIcon className="w-4 h-4" />}
                label={menu ? "Accounts" : undefined}
                onClick={() => navigate(`/home/${params.workspaceId}/view/${defaultView.accounts.id}`)}
                active={location.pathname.includes('/view/') && views.some(view => view.entity_relation === 'accounts' && location.pathname.includes(`/view/${view.id}`))}
              />
            )
          )}
          
          {defaultView?.users && (
            filterViews('users').length > 0 && menu ? (
              <MantineNavLink
              classNames={{
                collapse: 'relative',
              }}
                leftSection={<UserCircleIcon className="w-4 h-4" />}
                label={menu ? "Users" : undefined}
                onClick={() => handleToggle('users')}
                opened={opened.users}
                active={location.pathname.includes('/view/') && views.some(view => view.entity_relation === 'users' && location.pathname.includes(`/view/${view.id}`))}
                childrenOffset={0}
              >
                <MantineNavLink
                   classNames={{
                    root: `${location.pathname === `/home/${params.workspaceId}/view/${defaultView.users.id}` ? 'after:border-primary-200' : 'after:border-gray-200'} pl-8 after:border-l-2 after:border-b-2 relative after:absolute after:top-3.5 after:left-4 after:w-2.5 after:h-1.5 after:z-10 after:rounded-bl-full`,
                    label: 'capitalize'
                  }}
                  leftSection={<UserCircleIcon className="w-3.5 h-3.5" />}
                  label={menu ? "All Users" : undefined}
                  onClick={() => navigate(`/home/${params.workspaceId}/view/${defaultView.users.id}`)}
                  active={location.pathname === `/home/${params.workspaceId}/view/${defaultView.users.id}`}
                />
                {/* Custom vertical connector for Users with dynamic coloring */}
                {menu && (
                  <div className="absolute left-[16px] top-0 bottom-4 w-0.5 z-20">
                    {(() => {
                      const activePosition = getActiveViewPosition('users');
                      // Calculate position for colored line based on active view
                      
                      // If an item is active, render split colored line
                      if (activePosition >= 0) {
                        const activePositionPx = activePosition * 35;
                        return (
                          <>
                            <div 
                              className="absolute left-0 w-full z-10 bg-primary-200" 
                              style={{ top: 0, height: `${activePositionPx + 18}px` }}
                            />
                            <div 
                              className="absolute left-0 w-full bg-gray-200" 
                              style={{ top: `${activePositionPx + 10}px`, bottom: 0 }}
                            />
                          </>
                        );
                      }
                      
                      // Otherwise render full gray line
                      return <div className="absolute inset-0 bg-gray-200" />;
                    })()}
                  </div>
                )}

                {filterViews('users').length > 0 && menu && (
                  <>
                    {filterViews('users').map((view: any) => (
                      <MantineNavLink
                      classNames={{
                        root: `${location.pathname === `/home/${params.workspaceId}/view/${view.id}` ? 'after:border-primary-200' : 'after:border-gray-200'} pl-8 after:left-4 after:border-l-2 after:border-b-2 relative after:absolute after:top-3.5 after:w-2.5 after:h-1.5 after:z-10 after:rounded-bl-full`,
                        label: 'capitalize'
                      }}
                        key={view.id}
                        leftSection={
                          view.icon_key || view.view_icon ? (
                            <ViewIconDisplay 
                              iconType="built_in" 
                              iconKey={view.icon_key || view.view_icon} 
                              className="h-3.5 w-3.5 flex-shrink-0" 
                            />
                          ) : (
                            <UserCircleIcon className="w-3.5 h-3.5" />
                          )
                        }
                        label={menu ? view.view_name : undefined}
                        onClick={() => navigate(`/home/${params.workspaceId}/view/${view.id}`)}
                        active={location.pathname === `/home/${params.workspaceId}/view/${view.id}`}
                      />
                    ))}
                  </>
                )}
              </MantineNavLink>
            ) : (
              <MantineNavLink
                classNames={{
                  body: !menu ? 'hidden' : '',
                    section: !menu ? 'mx-auto' : '',
                  root: !menu ? 'items-center justify-center py-4' : '',
                }}
                leftSection={<UserCircleIcon className="w-4 h-4" />}
                label={menu ? "Users" : undefined}
                onClick={() => navigate(`/home/${params.workspaceId}/view/${defaultView.users.id}`)}
                active={location.pathname.includes('/view/') && views.some(view => view.entity_relation === 'users' && location.pathname.includes(`/view/${view.id}`))}
              />
            )
          )}
            </div>
            
            {menu && (
              <div className="flex items-center px-3 mt-4 gap-2">
                <Text size="9px" fw={500} c="dimmed">
                  Activities
                </Text>
              </div>
            )}
            <MantineNavLink
              leftSection={<BoltIcon className="w-5 h-5" />}
              classNames={{
                body: !menu ? 'hidden' : '',
                  section: !menu ? 'mx-auto' : '',
                root: !menu ? 'items-center justify-center py-4' : '',
              }}
              onClick={() => navigate(`/home/${params.workspaceId}/signals`)}
              label={menu ? "Signals" : undefined}
              active={location.pathname.includes('signals')}
            />
            
            <MantineNavLink
              leftSection={<ChatBubbleBottomCenterTextIcon className="w-5 h-5" />}
              classNames={{
                body: !menu ? 'hidden' : '',
                  section: !menu ? 'mx-auto' : '',
                root: !menu ? 'items-center justify-center py-4' : '',
              }}
              onClick={() => navigate(`/home/${params.workspaceId}/inbox`)}
              label={
                menu ? (
                  <div className="flex items-center justify-between w-full">
                    <span>Inbox</span>
                    {count > 0 && (
                      <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-0.5 rounded-full">
                        {count}
                      </span>
                    )}
                  </div>
                ) : undefined
              }
              active={location.pathname.includes('inbox')}
            />
          </div>
        </ScrollArea>
      </AppShell.Section>
      {menu && (
        <AppShell.Section>
          <div className="flex items-center px-3 mt-4 gap-2">
            <Text size="9px" fw={500} c="dimmed">
              Support
            </Text>
          </div>
          <MantineNavLink
            classNames={{
              body: !menu ? 'hidden' : '',
                section: !menu ? 'mx-auto' : '',
              root: !menu ? 'items-center justify-center py-4' : '',
            }}
            leftSection={<PlusCircleIcon className="w-4 h-4" />}
            onClick={() => redirect.redirectToOrgPage(workspace?.id)}
            label={
              <Indicator
                size={8}
                offset={-12}
                position="middle-end"
                color="violet"
                inline
                processing
              >
                <Text c="text.6" size="sm">
                  Invite Team
                </Text>
              </Indicator>
            }
          />
          <MantineNavLink
            component="a"
            href="https://docs.hyperengage.io/getting-started/start-here"
            target="_blank"
            classNames={{
              body: !menu ? 'hidden' : '',
                section: !menu ? 'mx-auto' : '',
              root: !menu ? 'items-center justify-center py-4' : '',
            }}
            leftSection={<QuestionMarkCircleIcon className="w-4 h-4" />}
            label={
              <Text c="text.6" size="sm">
                Help and docs
              </Text>
            }
          />
        </AppShell.Section>
      )}
    </>
  );
};

export default LeftSideBar;
